import { ArrowUpRight, Scale } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { SectionEyebrow } from "@/components/section-eyebrow";
import { GlassIcon } from "@/components/glass-icon";

type ComparisonItem = {
  title: string;
  description: string;
  href: string;
};

type Props = {
  items: ComparisonItem[];
  eyebrow?: string;
  title?: string;
};

function Card({ item }: { item: ComparisonItem }) {
  const inner = (
    <>
      <GlassIcon icon={Scale} size="sm" />
      <div>
        <h3 className="text-lg font-semibold text-white leading-snug text-balance group-hover:text-gradient transition-colors">
          {item.title}
        </h3>
        <p className="text-sm text-white/70 leading-relaxed mt-2">{item.description}</p>
      </div>
      <div className="mt-auto pt-2 inline-flex items-center gap-1.5 text-sm text-pink-soft group-hover:text-white transition-colors">
        Read comparison
        <ArrowUpRight className="size-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </div>
    </>
  );
  const className =
    "group glass-card p-6 flex flex-col gap-4 hover-lift hover:-translate-y-1 hover:border-white/25 transition-all duration-300";
  if (item.href.startsWith("/")) {
    return (
      <Link to={item.href} className={className}>
        {inner}
      </Link>
    );
  }
  return (
    <a href={item.href} target="_blank" rel="noopener noreferrer" className={className}>
      {inner}
    </a>
  );
}

export function ComparisonsSection({
  items,
  eyebrow = "How Axy.digital Compares",
  title = "See exactly how Axy.digital stacks up against the tools on your shortlist.",
}: Props) {
  if (!items || items.length === 0) return null;

  return (
    <section className="relative px-6 py-24 overflow-hidden">
      <div className="relative max-w-7xl mx-auto">
        <div className="max-w-3xl mb-10">
          <SectionEyebrow>{eyebrow}</SectionEyebrow>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white text-balance">
            {title}
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((item) => (
            <Card key={item.href} item={item} />
          ))}
        </div>
      </div>
    </section>
  );
}

